import { useEffect, useState } from 'react';
import {
  canvasAddressKeys, ledgerLine, ledgerPath, transfersNotOnCanvas, type LedgerCounterparty,
} from '../../utils/ledgerTransfers';

const API_BASE_URL = (import.meta.env as unknown as Record<string, string>).VITE_API_URL || 'http://localhost:8000/api/v1';
const LIST_LIMIT = 50;

type CyNodes = {
  nodes?: () => { map: <T>(f: (e: { data: () => Record<string, unknown> }) => T) => T[] };
  destroyed?: () => boolean;
};

interface LedgerRow {
  tx_hash?: string; leg?: string; token?: string; amount?: number | null;
  timestamp?: string | null; tx_type?: string; direction?: string | null;
}

function canvasKeys(): Set<string> {
  const cy = (window as unknown as { __fusionCy?: CyNodes }).__fusionCy;
  if (!cy || (cy.destroyed && cy.destroyed()) || !cy.nodes) return new Set();
  return canvasAddressKeys(cy.nodes().map((n) => n.data()));
}

async function getJson(path: string): Promise<Record<string, unknown> | null> {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  return (await res.json()) as Record<string, unknown>;
}

/**
 * LEDGER01: "Transfers in ledger: n (k not on canvas)" for a wallet node of the case canvas. n is the transfer
 * ledger's count for the wallet (`GET /cases/{id}/transfers?wallet=`), k the transfers whose other side is not a node
 * on the canvas. Clicking the line lists the first rows of the endpoint's result for the wallet. Nothing is shown for
 * a node that is not a wallet, or when the ledger holds nothing for it (the line is not a claim of absence).
 */
export function LedgerTransfersLine({ data, caseId }: { data: Record<string, unknown>; caseId?: string }) {
  const address = typeof data.address === 'string' ? data.address : '';
  const chain = typeof data.chain === 'string' ? data.chain : undefined;
  const [total, setTotal] = useState<number | null>(null);
  const [off, setOff] = useState(0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<LedgerRow[] | null>(null);

  useEffect(() => {
    // R8: a node change must not show another node's count
    let cancelled = false;
    setTotal(null); setOff(0); setOpen(false); setRows(null);
    if (!caseId || !address) return;
    getJson(ledgerPath(caseId, address, chain, 1))
      .then((body) => {
        if (cancelled || !body) return;
        setTotal(Number(body.total) || 0);
        setOff(transfersNotOnCanvas(body.counterparties as LedgerCounterparty[] | undefined, canvasKeys()));
      })
      .catch(() => { /* the line is additive: no count, no line */ });
    return () => { cancelled = true; };
  }, [caseId, address, chain]);

  useEffect(() => {
    if (!open || !caseId || !address || rows !== null) return;
    let cancelled = false;
    getJson(ledgerPath(caseId, address, chain, LIST_LIMIT))
      .then((body) => { if (!cancelled) setRows(Array.isArray(body?.rows) ? (body!.rows as LedgerRow[]) : []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [open, caseId, address, chain, rows]);

  if (!caseId || !address || !total) return null;
  return (
    <div className="mt-2 border-t border-border pt-2" data-testid="fusion-ledger-line">
      <button
        type="button"
        className="text-[11px] underline decoration-dotted text-left"
        data-testid="fusion-ledger-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {ledgerLine(total, off)}
      </button>
      {open && (
        <div className="mt-1 max-h-48 overflow-y-auto text-[10px]" data-testid="fusion-ledger-rows">
          {rows === null ? 'Loading...' : rows.length === 0 ? 'No rows.' : (
            <table className="w-full">
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.tx_hash}:${r.leg}`} data-tx-type={r.tx_type}>
                    <td className="pr-1">{r.timestamp ? r.timestamp.slice(0, 16).replace('T', ' ') : '-'}</td>
                    <td className="pr-1">{r.direction || '-'}</td>
                    <td className="pr-1 text-right">{r.amount == null ? '-' : r.amount} {r.token}</td>
                    <td className="pr-1">{r.tx_type}</td>
                    <td className="font-mono">{String(r.tx_hash || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
